<template>
  <div v-if="visible" class="md-agents">
    <div class="md-agents__backdrop" @click="$emit('close')"></div>
    <div class="md-agents__panel">
      <div class="md-agents__grip"></div>
      <div class="md-agents__head">
        <div class="md-agents__heading">
          <h3 class="md-agents__title">{{ desk }}</h3>
          <div class="md-agents__sub">{{ $t('mobileDash.deskAgents') }}</div>
        </div>
        <button type="button" class="md-agents__close" @click="$emit('close')">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div v-if="tabs.length > 1" class="md-agents__tabs">
        <button
            v-for="t in tabs" :key="t.key"
            type="button"
            class="md-agents__tab"
            :class="{ 'is-active': activeTab === t.key }"
            @click="tab = t.key"
        >
          {{ t.label }}
        </button>
      </div>

      <div class="md-agents__body">
        <md-skeleton v-if="loading" variant="list" :rows="6"/>
        <div v-else-if="error" class="md-agents__error">
          <div>{{ error }}</div>
          <b-button size="sm" variant="outline-primary" @click="fetch">{{ $t('mobileDash.retry') }}</b-button>
        </div>
        <div v-else-if="!items.length" class="md-agents__empty">{{ $t('mobileDash.noData') }}</div>
        <md-rank-list v-else :items="items" :limit="8" selectable @select="item => $emit('select', item)"/>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import MdSkeleton from '@/components/mobile/MdSkeleton';
import MdRankList from '@/components/mobile/MdRankList';
import { fullEuro } from '@/components/mobile/mdFormat';

/**
 * Per-desk agent drill: tapping a desk card opens this sheet with the desk's
 * sales/retention agent tables (one backend call brings both, the tabs switch
 * client-side). Tapping an agent bubbles `select` up so the dashboard can open
 * the transactions drill on top, scoped to the desk + agent.
 */
export default {
  name: 'MdAgentSheet',
  components: { MdSkeleton, MdRankList },
  mixins: [mixinBase],
  props: {
    visible: { type: Boolean, default: false },
    desk: { type: String, default: '' },
    // The dashboard's active filter (range / department / org / BU); the
    // sheet swaps in its own desk before calling the backend.
    filters: { type: Object, default: () => ({}) },
  },
  data: () => ({
    tab: 'sales',
    data: null,
    loading: false,
    error: null,
  }),
  computed: {
    tabs() {
      const tabs = [];
      if (((this.data || {}).salesAgents || []).length) {
        tabs.push({ key: 'sales', label: this.$t('mobileDash.sales') });
      }
      if (((this.data || {}).retentionAgents || []).length) {
        tabs.push({ key: 'retention', label: this.$t('mobileDash.retention') });
      }
      // The desk card's TXF figure, split per depositor
      if (((this.data || {}).txfAgents || []).length) {
        tabs.push({ key: 'txf', label: 'TXF' });
      }
      return tabs;
    },
    activeTab() {
      const tabs = this.tabs;
      if (!tabs.length) return null;
      return tabs.some(t => t.key === this.tab) ? this.tab : tabs[0].key;
    },
    items() {
      const tab = this.activeTab;
      if (!tab) return [];
      if (tab === 'txf') {
        return ((this.data || {}).txfAgents || []).map(row => ({
          key: row.userId || 'none',
          label: row.name || this.$t('commandCenter.txfNoAgent'),
          stats: [{ value: row.count || 0, label: this.$t('mobileDash.drill.items'), tone: 'txf' }],
          value: fullEuro(row.txf || 0),
          raw: { ...row, kind: tab },
        }));
      }
      const rows = (this.data || {})[tab === 'sales' ? 'salesAgents' : 'retentionAgents'] || [];
      return rows.map(row => ({
        key: row.userId || row.name,
        label: row.name,
        stats: tab === 'sales'
            ? this.salesStats(row)
            : [
              { value: row.count || 0, label: this.$t('mobileDash.depositsLabel'), tone: 'default' },
              ...(row.std ? [{ value: row.std, label: 'STD', tone: 'std' }] : []),
              // clients handed to this retention agent in range — the sales
              // dashboard's own assignment-log figure (receivedCount), so the
              // badge always matches the retention table there.
              ...(row.receivedCount != null
                  ? [{ value: row.receivedCount, label: this.$t('salesDashboard.received'), tone: 'leads' }]
                  : []),
            ],
        value: fullEuro(row.deposit || 0),
        raw: { ...row, kind: tab },
      }));
    },
  },
  watch: {
    visible(open) {
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        this.tab = 'sales';
        this.data = null;
        this.error = null;
        this.fetch();
      }
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  methods: {
    ...mapActions('mobileDashboard', ['getDeskAgents']),
    // Sales rows mirror the sales dashboard's FTD funnel: leads received in
    // range (assignment-log receivedCount, legacy pending-leads fallback) →
    // FTD count → conversion % — same figures, same datasource.
    salesStats(row) {
      const leads = row.receivedCount != null ? row.receivedCount : (row.leads || 0)
      const stats = [
        { value: row.count || 0, label: 'FTD', tone: 'ftd' },
        { value: leads, label: this.$t('mobileDash.leadsLabel'), tone: 'leads' },
      ]
      if (leads) {
        stats.push({
          value: Math.round((row.count || 0) / leads * 100) + '%',
          label: this.$t('mobileDash.conversion'),
          tone: 'conv',
        })
      }
      return stats
    },
    fetch() {
      this.loading = true;
      this.error = null;
      this.getDeskAgents({ ...this.filters, desks: [this.desk] })
          .then(data => {
            this.data = data;
          })
          .catch(err => {
            this.error = this.getErrorDescription(err);
          })
          .finally(() => {
            this.loading = false;
          });
    },
  },
};
</script>

<style scoped>
/* Same sheet chrome as MdDrillSheet, one z-index layer below it so the
   transactions drill opened from an agent row stacks on top. */
.md-agents__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 20, 26, 0.5);
  z-index: 1080;
  animation: md-agents-fade 0.2s ease;
}
.md-agents__panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1081;
  background: var(--md-card-bg, #fff);
  border-radius: 16px 16px 0 0;
  height: 82vh;
  display: flex;
  flex-direction: column;
  animation: md-agents-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  padding-bottom: env(safe-area-inset-bottom, 0);
}
.md-agents__grip {
  width: 38px;
  height: 4px;
  border-radius: 999px;
  background: rgba(138, 145, 156, 0.35);
  margin: 8px auto 0;
}
.md-agents__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.6rem 1rem 0.55rem;
  border-bottom: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
}
.md-agents__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.md-agents__sub {
  margin-top: 2px;
  font-size: 11.5px;
  color: var(--md-muted, #8a919c);
}
.md-agents__close {
  border: none;
  background: rgba(138, 145, 156, 0.12);
  width: 30px;
  height: 30px;
  border-radius: 999px;
  font-size: 14px;
  color: var(--md-muted, #8a919c);
  flex: 0 0 auto;
}
.md-agents__tabs {
  display: flex;
  gap: 6px;
  padding: 0.55rem 1rem 0;
}
.md-agents__tab {
  border: none;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(138, 145, 156, 0.12);
  color: var(--md-muted, #8a919c);
}
.md-agents__tab.is-active {
  background: rgba(115, 103, 240, 0.14);
  color: #7367f0;
}
.md-agents__body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.5rem 0.75rem 1rem;
}
.md-agents__error,
.md-agents__empty {
  text-align: center;
  padding: 1.6rem 1rem;
  font-size: 12.5px;
  color: var(--md-muted, #8a919c);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: center;
}
@keyframes md-agents-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes md-agents-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.dark-layout .md-agents__panel {
  --md-card-bg: #283046;
  --md-card-border: #3b4253;
  --md-strong: #d0d2d6;
}
</style>
